import { useState, useEffect } from "react";
import { LogOut, Lock, Eye, EyeOff, Users, Search, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("admin_token", res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao autenticar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl font-bold text-[#E5E7EB]">
            <span className="text-[#F59E0B]">Kp</span>Action
          </h1>
          <p className="text-sm text-[#6B7280] mt-2">Painel Administrativo</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm text-[#9CA3AF]">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="admin-login-email"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-[#9CA3AF]">Senha</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] focus:border-[#F59E0B] focus:ring-[#F59E0B] pr-10"
                data-testid="admin-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#9CA3AF]"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#F59E0B] text-black font-bold hover:bg-[#D97706] rounded-sm"
            data-testid="admin-login-submit"
          >
            {loading ? "Entrando..." : "Entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function ChangePasswordModal({ isOpen, onClose, token }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("A nova senha deve ter no mínimo 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Senha alterada com sucesso");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6 w-full max-w-sm">
        <h3 className="font-heading text-lg font-bold text-[#E5E7EB] mb-4">Alterar Senha</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm text-[#9CA3AF]">Senha Atual</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="change-password-current"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-[#9CA3AF]">Nova Senha</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="change-password-new"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-[#9CA3AF]">Confirmar Nova Senha</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="change-password-confirm"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-[#2A2F38] text-[#9CA3AF] hover:bg-[#2A2F38]">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-[#F59E0B] text-black font-bold hover:bg-[#D97706]" data-testid="change-password-submit">
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LeadsTable({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sessão expirada");
        onLogout();
      } else {
        toast.error("Erro ao carregar leads");
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = leads.filter((lead) => {
    const q = search.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(q) ||
      lead.email?.toLowerCase().includes(q) ||
      lead.company?.toLowerCase().includes(q) ||
      lead.cnpj?.includes(q) ||
      lead.phone?.includes(q)
    );
  });

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const exportCSV = () => {
    if (filtered.length === 0) return;
    const headers = ["Nome", "Email", "Empresa", "CNPJ", "Cargo", "Telefone", "Funcionários", "Data"];
    const rows = filtered.map((lead) => [
      lead.name,
      lead.email,
      lead.company,
      lead.cnpj,
      lead.position,
      lead.phone,
      lead.employees,
      formatDate(lead.created_at),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${(cell || "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `kpaction-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0F1115]">
      <header className="border-b border-[#2A2F38] bg-[#1C1F26]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-xl font-bold">
              <span className="text-[#F59E0B]">Kp</span><span className="text-[#E5E7EB]">Action</span>
            </h1>
            <span className="text-xs text-[#6B7280] border-l border-[#2A2F38] pl-3">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowChangePassword(true)}
              className="border-[#2A2F38] text-[#9CA3AF] hover:bg-[#2A2F38] hover:text-[#E5E7EB]"
              data-testid="admin-change-password-btn"
            >
              <Lock className="h-4 w-4 mr-1" />
              Alterar Senha
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              className="border-[#2A2F38] text-[#9CA3AF] hover:bg-[#2A2F38] hover:text-[#E5E7EB]"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-[#F59E0B]" />
            <h2 className="font-heading text-xl font-bold text-[#E5E7EB]">Leads Cadastrados</h2>
            <span className="text-sm text-[#6B7280]">({total})</span>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={exportCSV} disabled={filtered.length === 0} className="border-[#2A2F38] text-[#9CA3AF] hover:bg-[#2A2F38] hover:text-[#E5E7EB] rounded-sm" data-testid="admin-export-csv-btn">
              <Download className="h-4 w-4 mr-1" />
              Exportar CSV
            </Button>
            <Button size="sm" onClick={fetchLeads} className="bg-[#F59E0B] text-black font-bold hover:bg-[#D97706] rounded-sm" data-testid="admin-refresh-btn">
              Atualizar
            </Button>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
          <Input
            type="text"
            placeholder="Buscar por nome, email, empresa ou CNPJ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-[#1C1F26] border-[#2A2F38] text-[#E5E7EB] pl-10 focus:border-[#F59E0B] focus:ring-[#F59E0B]"
            data-testid="admin-search-input"
          />
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#6B7280]">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#6B7280]">
            {search ? "Nenhum resultado encontrado." : "Nenhum lead cadastrado ainda."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-[#2A2F38]">
                  <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#6B7280]">Nome</th>
                  <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#6B7280]">Email</th>
                  <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#6B7280]">Empresa</th>
                  <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#6B7280]">CNPJ</th>
                  <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#6B7280]">Cargo</th>
                  <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#6B7280]">Telefone</th>
                  <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#6B7280]">Func.</th>
                  <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#6B7280]">Data</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((lead, i) => (
                  <tr key={lead.id || i} className="border-b border-[#2A2F38]/50 hover:bg-[#1C1F26]/50 transition-colors">
                    <td className="py-3 px-4 text-sm text-[#E5E7EB]">{lead.name}</td>
                    <td className="py-3 px-4 text-sm text-[#F59E0B]">{lead.email}</td>
                    <td className="py-3 px-4 text-sm text-[#9CA3AF]">{lead.company}</td>
                    <td className="py-3 px-4 text-sm text-[#9CA3AF] font-mono text-xs">{lead.cnpj}</td>
                    <td className="py-3 px-4 text-sm text-[#9CA3AF]">{lead.position}</td>
                    <td className="py-3 px-4 text-sm text-[#9CA3AF]">{lead.phone}</td>
                    <td className="py-3 px-4 text-sm text-[#9CA3AF]">{lead.employees}</td>
                    <td className="py-3 px-4 text-sm text-[#6B7280] whitespace-nowrap">{formatDate(lead.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        token={token}
      />
    </div>
  );
}

export default function AdminLeadsPage() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));

  const handleLogin = (newToken) => {
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return <LeadsTable token={token} onLogout={handleLogout} />;
}
